import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { CosmosCustomer } from '../models/customer';
import { PortalAccess } from '../models/portal-access';

@Injectable()
export class SecurityService {
  private b2cUser: AccountInfo;
  private portalAccess: PortalAccess;
  private readonly currentAccount = new BehaviorSubject<CosmosCustomer>(null);
  private currentAccountObject: CosmosCustomer = null;

  public readonly $authorizationStartupInProgress = new BehaviorSubject<boolean>(true);

  public setUser(user: AccountInfo): void {
    this.b2cUser = user;
  }

  public getUser(): AccountInfo {
    return this.b2cUser;
  }

  public setPortalAccess(access: PortalAccess): void {
    this.portalAccess = access ? {
      ...access,
      customerAxCodes: this.normalizeCustomerAxCodes(access.customerAxCodes)
    } : null;
  }

  public hasPortalAccess(): boolean {
    return this.portalAccess?.status === 'active';
  }

  public getCustomerAxCodes(): string[] {
    return [...(this.portalAccess?.customerAxCodes ?? [])];
  }

  public getUserDisplayName(): string {
    return this.portalAccess?.displayName ?? '';
  }

  public hasCustomerAccess(axCode: string): boolean {
    const normalizedAxCode = this.normalizeCustomerAxCode(axCode);
    return normalizedAxCode.length > 0
      && this.portalAccess?.customerAxCodes?.includes(normalizedAxCode) === true;
  }

  public getUserEmail(): string | null {
    return this.portalAccess?.email?.trim() || null;
  }

  public reset(): void {
    this.b2cUser = null;
    this.portalAccess = null;
    this.currentAccount.next(null);
    this.currentAccountObject = null;
    this.$authorizationStartupInProgress.next(true);
  }

  public setCurrentAccount(currentAccount: CosmosCustomer): void {
    this.currentAccount.next(currentAccount);
    this.currentAccountObject = currentAccount;
  }

  public getCurrentAccount(): Observable<CosmosCustomer> {
    return this.currentAccount;
  }

  public getCurrentAccountObject(): CosmosCustomer {
    return this.currentAccountObject;
  }

  public hasClaim(claimType: string | string[], claimValue?: string): boolean {
    if (typeof claimType === 'string') {
      return this.isClaimValid(claimType, claimValue);
    }

    return claimType?.some(claim => this.isClaimValid(claim)) === true;
  }

  public hasSelectedContext(): boolean {
    return this.currentAccountObject != null;
  }

  private isClaimValid(claimType: string, claimValue?: string): boolean {
    const auth = this.b2cUser;
    if (!auth) {
      return false;
    }

    if (claimType.includes(':')) {
      const words = claimType.split(':');
      claimType = words[0].toLowerCase();
      claimValue = words[1];
    } else {
      claimType = claimType.toLowerCase();
      claimValue = claimValue || 'true';
    }

    return auth.idTokenClaims?.[claimType] === claimValue;
  }

  private normalizeCustomerAxCodes(axCodes: string[]): string[] {
    return [...new Set((axCodes ?? [])
      .map(axCode => this.normalizeCustomerAxCode(axCode))
      .filter(Boolean))];
  }

  private normalizeCustomerAxCode(axCode: string): string {
    return axCode?.trim().toUpperCase() ?? '';
  }
}
